/**
 * HelpOverlay — Wiederverwendbares Tastatur-Shortcut-Overlay.
 *
 * Verwendung:
 *   const { isOpen, close } = useHelpOverlay();
 *   <HelpOverlay isOpen={isOpen} onClose={close} title="..." sections={[
 *     { title: 'Navigation', shortcuts: [
 *        { keys: ['?'], label: 'Diese Hilfe öffnen / schließen' },
 *        { keys: ['Esc'], label: 'Schließen' },
 *     ]},
 *   ]} />
 *
 * Hook registriert globalen `?`-Listener (skippt Inputs/Textareas).
 * Esc schließt das Overlay automatisch.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X } from 'lucide-react';

export function useHelpOverlay({ enabled = true } = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => {
      const t = e.target;
      const inInput = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '?' && !inInput) {
        e.preventDefault();
        toggle();
      } else if (e.key === 'Escape' && isOpen) {
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, isOpen, toggle, close]);

  return { isOpen, open, close, toggle };
}

export default function HelpOverlay({ isOpen, onClose, title = 'Tastatur-Shortcuts', sections = [] }) {
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="help-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="help-overlay-close"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {sections.map((section, i) => (
          <div key={i} className={i > 0 ? 'mt-5' : ''}>
            {section.title && (
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {section.title}
              </div>
            )}
            <div className="space-y-1.5">
              {section.shortcuts.map((s, j) => (
                <ShortcutRow key={j} {...s} />
              ))}
            </div>
          </div>
        ))}

        <div className="mt-5 pt-4 border-t border-slate-200 text-[11px] text-slate-500">
          Tipp: Diese Hilfe lässt sich jederzeit mit{' '}
          <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-mono text-[10px]">?</kbd> öffnen.
        </div>
      </div>
    </div>
  );
}

function ShortcutRow({ keys, label }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-slate-700 flex-1">{label}</span>
      <span className="flex items-center gap-1 flex-shrink-0">
        {keys.map((k, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="text-slate-300 text-xs">+</span>}
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono text-[11px] text-slate-700">
              {k}
            </kbd>
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}

/**
 * HelpButton — kleiner „?"-Trigger, der das Overlay manuell öffnet.
 * Sinnvoll für Discoverability (User, die den `?`-Shortcut nicht kennen).
 */
export function HelpButton({ onClick, className = '', size = 'sm', testId = 'help-trigger' }) {
  const sizeClasses = size === 'sm' ? 'h-7 w-7 text-xs' : 'h-9 w-9 text-sm';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors font-mono font-bold ${sizeClasses} ${className}`}
      data-testid={testId}
      aria-label="Hilfe öffnen"
      title="Tastatur-Shortcuts (?)"
    >
      ?
    </button>
  );
}
